import { useMutation }                      from '@apollo/client'
import { PlusIcon, XMarkIcon }              from '@heroicons/react/20/solid'
import { ArrowUturnLeftIcon }               from '@heroicons/react/24/solid'
import React, { useState }                  from 'react'
import { useDispatch }                      from 'react-redux'
import { useNavigate }                      from 'react-router-dom'
import { Button, TextField }                from '../'
import { setLocationsListRefetch }          from '../../features/user/currentUserSlice'
import { setToasts, showToaster }           from '../../features/toaster/toasterSlice'
import { CREATE_LOCATION, UPDATE_LOCATION } from '../../graphql/location'
import { LoadingEllipsis }                  from '../Loaders'

export default function LocationEditForm( props: any ): JSX.Element {
  const { location }              = props
  const navigate: any             = useNavigate()
  const dispatch: any             = useDispatch()
  const [ formData, setFormData ] = useState( {
                                                name:            location?.name || '',
                                                email:           location?.email || '',
                                                telephone:       location?.telephone || '',
                                                addressLine1:    location?.address_line1 || '',
                                                addressLine2:    location?.address_line2 || '',
                                                addressCity:     location?.address_city || '',
                                                addressCounty:   location?.address_county || '',
                                                addressPostcode: location?.address_postcode || '',
                                                reportData:      location?.report_data || [],
                                                _newReportName:  '',
                                                _newReportUrl:   ''
                                              } )
  const [ errors, setErrors ]     = useState( {
                                                name:            false,
                                                email: false,
                                                addressLine1:    false,
                                                addressCity:     false,
                                                addressPostcode: false
                                              } )

  const updateField: any = ( field: keyof typeof formData, value: string ): any => {
    setFormData( prev => ( { ...prev, [ field ]: value } ) )
  }

  const addReportItem: any = ( item: { url: string; name: string } ): any => {
    setFormData( prev => ( {
      ...prev,
      reportData: [ ...prev.reportData, item ]
    } ) )
  }

  const updateReportItem: any = ( item: any, index: number ): any => {
    setFormData( prev => ( {
      ...prev,
      reportData: prev.reportData.map( ( el: any, i: number ): any => {
        if ( i === index ) {
          return item
        }
        return el
      } )
    } ) )
  }

  const removeReportItem: any = ( index: number ) => {
    setFormData( ( prev: any ): any => ( {
      ...prev,
      reportData: prev.reportData.filter( ( _: any, i: number ): any => i !== index )
    } ) )
  }

  const checkErrors: any = (): boolean => {
    const newErrors = {
      name:            !formData.name.trim(),
      email:           !formData.email.trim(),
      addressLine1:    !formData.addressLine1.trim(),
      addressCity:     !formData.addressCity.trim(),
      addressPostcode: !formData.addressPostcode.trim()
    }

    setErrors( newErrors )

    return Object.values( newErrors ).some( Boolean )
  }

  const [ createLocation, { loading: creating } ] = useMutation( CREATE_LOCATION )
  const [ updateLocation, { loading: updating } ] = useMutation( UPDATE_LOCATION )

  const dispatchMessage: any = ( type: string, message: string ): void => {
    dispatch( setToasts( [ { id: '1', type, message } ] ) )
    dispatch( showToaster( true ) )
  }

  const handleSubmit = async (): Promise<void> => {
    if ( checkErrors() ) return

    const newReportName: string = formData._newReportName.trim()
    const newReportUrl: string  = formData._newReportUrl.trim()

    if ( ( newReportName && !newReportUrl ) || ( !newReportName && newReportUrl ) ) {
      dispatchMessage( 'error', 'Complete both Report Name and Report URL before submitting' )
      return
    }

    const reportData: any[] = ( newReportName && newReportUrl )
      ? [ ...formData.reportData, { name: newReportName, url: newReportUrl } ]
      : formData.reportData

    const input = {
      name:             formData.name,
      email:            formData.email,
      telephone:        formData.telephone,
      report_data:      reportData,
      address_line1:    formData.addressLine1,
      address_line2:    formData.addressLine2,
      address_city:     formData.addressCity,
      address_county:   formData.addressCounty,
      address_postcode: formData.addressPostcode
    }

    try {
      if ( location?.id ) {
        const { data }: any = await updateLocation( { variables: { input: { ...input, id: location.id } } } )
        const result: any   = data?.updateLocation

        if ( result?.__typename !== 'MutationUpdateLocationSuccess' ) {
          dispatchMessage( 'error', result?.message ?? 'Update failed. Please try again' )
          return
        }

        setFormData( ( prev: any ): any => ( {
          ...prev,
          reportData,
          _newReportName: '',
          _newReportUrl:  ''
        } ) )
        dispatch( setLocationsListRefetch( true ) )
        dispatchMessage( 'success', 'Location updated!' )
      } else {
        const { data }: any = await createLocation( { variables: { input } } )
        const result: any   = data?.createLocation

        if ( result?.__typename !== 'MutationCreateLocationSuccess' ) {
          dispatchMessage( 'error', result?.message ?? 'Location NOT added. Please try again' )
          return
        }

        dispatch( setLocationsListRefetch( true ) )
        dispatchMessage( 'success', 'Location added successfully!' )
        navigate( '/locations' )
      }
    } catch ( e ) {
      console.log( 'e: ', e )
      dispatchMessage( 'error', 'Save failed. Please try again' )
    }
  }

  return (
    <>
      <div className="w-24 my-4">
        <Button
          label="Back"
          blue
          onClick={ (): any => navigate( -1 ) }
          icon={ <ArrowUturnLeftIcon className="w-4 h-4" /> }
        />
      </div>

      { ( creating || updating ) && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4" /> }

      { !creating && !updating &&
        <div className="flex flex-col items-center w-full bg-white rounded-3xl p-4 xl:p-5">
          <div className="flex flex-wrap items-center w-full mb-3 pt-2">
            <div className="w-1/3">Name</div>
            <div className="w-2/3">
              <TextField
                label={ 'Name' }
                value={ formData.name }
                name="title"
                type="text"
                onChange={ ( val: string ): any => updateField( 'name', val ) }
                errorField={ errors.name ? 'name' : '' }
                isRequired
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Email</div>
            <div className="w-2/3">
              <TextField
                label={ 'Email' }
                value={ formData.email }
                name="email"
                type="text"
                onChange={ ( val: string ): any => updateField( 'email', val ) }
                errorField={ errors.email ? 'email' : '' }
                isRequired
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Telephone</div>
            <div className="w-2/3">
              <TextField
                label={ 'Telephone' }
                value={ formData.telephone }
                name="telephone"
                type="tel"
                onChange={ ( val: string ): any => updateField( 'telephone', val ) }
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Address Line 1</div>
            <div className="w-2/3">
              <TextField
                label={ 'Address Line 1' }
                value={ formData.addressLine1 }
                name="addressLine1"
                type="text"
                onChange={ ( val: string ): any => updateField( 'addressLine1', val ) }
                errorField={ errors.addressLine1 ? 'addressLine1' : '' }
                isRequired
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Address Line 2</div>
            <div className="w-2/3">
              <TextField
                label={ 'Address Line 2' }
                value={ formData.addressLine2 }
                name="addressLine2"
                type="text"
                onChange={ ( val: string ): any => updateField( 'addressLine2', val ) }
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Address City</div>
            <div className="w-2/3">
              <TextField
                label={ 'Address City' }
                value={ formData.addressCity }
                name="addressCity"
                type="text"
                onChange={ ( val: string ): any => updateField( 'addressCity', val ) }
                errorField={ errors.addressCity ? 'addressCity' : '' }
                isRequired
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Address County</div>
            <div className="w-2/3">
              <TextField
                label={ 'Address County' }
                value={ formData.addressCounty }
                name="addressCounty"
                type="text"
                onChange={ ( val: string ): any => updateField( 'addressCounty', val ) }
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center w-full mb-3">
            <div className="w-1/3">Address Postcode</div>
            <div className="w-2/3">
              <TextField
                label={ 'Address Postcode' }
                value={ formData.addressPostcode }
                name="addressPostcode"
                type="text"
                onChange={ ( val: string ): any => updateField( 'addressPostcode', val ) }
                errorField={ errors.addressPostcode ? 'addressPostcode' : '' }
                isRequired
              />
            </div>
          </div>

          <div className="w-full mt-4">
            <h3 className="font-semibold mb-4">Report Data</h3>

            <ul className="mb-4">
              { formData.reportData.map( ( report: any, i: number ): any => (
                <li key={ i } className="flex items-center justify-between mb-2 gap-4">
                  <TextField
                    label="Report Name"
                    name={ `report_name_${ i }` }
                    type="text"
                    value={ report.name }
                    onChange={ ( val: string ): any => updateReportItem( { ...report, name: val }, i ) }
                  />
                  <TextField
                    label="Report URL"
                    name={ `report_url_${ i }` }
                    type="text"
                    value={ report.url }
                    onChange={ ( val: string ): any => updateReportItem( { ...report, url: val }, i ) }
                  />
                  <button
                    className="flex items-center justify-center rounded-lg text-white bg-red-500 w-8 h-8"
                    onClick={ (): any => removeReportItem( i ) }
                  >
                    <XMarkIcon />
                  </button>
                </li>
              ) ) }
            </ul>

            <div className="flex gap-4 mb-3">
              <TextField
                label="Report Name"
                name="report_name_new"
                type="text"
                value={ formData._newReportName || '' }
                onChange={ ( val: string ): any => setFormData( ( prev: any ): any => ( { ...prev, _newReportName: val } ) ) }
              />
              <TextField
                label="Report URL"
                name="report_url_new"
                type="text"
                value={ formData._newReportUrl || '' }
                onChange={ ( val: string ): any => setFormData( ( prev: any ): any => ( { ...prev, _newReportUrl: val } ) ) }
              />
              <button
                className="flex items-center justify-center rounded-lg text-white bg-green-500 w-8 h-8"
                onClick={ (): any => {
                  if ( formData._newReportName && formData._newReportUrl ) {
                    addReportItem( { url: formData._newReportUrl, name: formData._newReportName } )
                    setFormData( ( prev: any ): any => ( { ...prev, _newReportName: '', _newReportUrl: '' } ) )
                  }
                } }
              >
                <PlusIcon />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap w-full">
            <div className="flex pt-5">
              <Button label="Submit" yellow onClick={ handleSubmit } />
            </div>
          </div>
        </div>
      }
    </>
  )
}
